import { serialize } from '@shoelace-style/shoelace/dist/utilities/form.js';

export const FormHostMixin = (SuperClass) => class extends SuperClass {

  // FIXME >> Refactor other parts of code base to use this. Contemplate renaming this file.

  /**
   * Overwrite this getter to add post processing steps fired after input based on field name.
   * These are invoked after handleChange() AND after handleInput().
   */
  get handleInputPostProcess() {
    return {
      // EXAMPLE:
      // emailsAdditionalText: (newValue, targetElement)
      //   => this.handleEmailsAdditionalTextChange(newValue),
    };
  }

  /**
   * Base event form handler. Maps input name to component instance variable.
   * Use EITHER handleChange() or handleInput(). They are equivalent and using
   * both would be redundant.
   * 
   * Example Usage:
   * ```html
   * <sl-input name="nameInternal" \@sl-change="${this.handleChange}"></sl-input>
   * ```
   */
  handleChange(event) {
    this.handleInput(event);
  }

  /**
   * Base event form handler. Maps input name to component instance variable.
   * Supports dot-notation for setting nested object fields.
   * 
   * Example Usage:
   * ```html
   * <sl-input name="nameInternal" \@sl-input=${this.handleInput}></sl-input>
   * <sl-input name="responseData.name" \@sl-input=${this.handleInput}></sl-input>
   * ```
   */
  handleInput(event, valueFunction = ((target) => target.value)) {
    const fieldName = event.target.name || event.target.id; // id needed for non-form data fields
    const fieldValue = valueFunction(event.target);

    if (!!fieldName) {
      if (fieldName.includes('.')) this._setNestedProperty(this, fieldName, fieldValue);
      else this[fieldName] = fieldValue;

      if (this.handleInputPostProcess[fieldName]) {
        this.handleInputPostProcess[fieldName](fieldValue, event.target);
      }
    }
  }

  /**
   * Alternate version designed to listen for changes on checkboxes.
   * 
   * Example Usage:
   * ```html
   * <sl-checkbox name="agreeToTerms" \@sl-change="${this.handleChecked}"></sl-checkbox>
   * ```
   */
  handleChecked(event) {
    this.handleInput(event, (target) => !!target.checked);
  }

  /**
   * Handles input for fields that should be treated as arrays.
   * 
   * Example Usage:
   * ```html
   * <sl-input name="tags" \@sl-input=${this.handleArray}></sl-input>
   * ```
   */
  handleArray(event) {
    this.handleInput(
      event,
      target => target.value.split(',').map(i => i.trim()).filter(i => i)
    );
  }

  /**
   * Handles input for fields that should be treated as JSON formatted string representations
   * of object values. Ideally this is a textarea or another long text input. The text value
   * will be parsed into an object using JSON.parse().
   * 
   * The destination field should be an object.
   * 
   * Example Usage:
   * ```html
   * <sl-textarea name="memberObject" \@sl-input=${this.handleObject}></sl-textarea>
   * ```
   */
  handleObject(event) {
    this.handleInput(
      event,
      target => {
        try {
          return JSON.parse(target.value);
        } catch (error) {
          console.error(`Invalid JSON in ${target.name}:`, error, target);
          return {};
        }
      }
    );
  }

  /**
   * Sets a nested property on an object given a dot-separated path or an array of keys.
   *
   * @param {Object} targetObject - The object on which the property should be set.
   * @param {string|string[]} path - The path to the property as a dot-separated string or
   *   an array of keys.
   * @param {*} value - The value to set at the specified path.
   */
  _setNestedProperty(targetObject, path, value) {
    const pathArray = Array.isArray(path) ? path : path.split('.');

    pathArray.reduce((acc, key, index) => {
      if (index === pathArray.length - 1) {
        // If we're at the end of the path array, then write the value
        acc[key] = value;
      } else if (!acc[key] || typeof acc[key] !== 'object') {
        // If the current path step is empty or not an object, overwrite with an empty object
        acc[key] = {};
      }

      // Next acc value will go down in depth
      return acc[key];
    }, targetObject);
  }

  /**
   * Serializes a form and returns a plain object. If a form control with the same name appears 
   * more than once, the property will be converted to an array.
   */
  _serializeForm(formElement) {
    return serialize(formElement);
  }

}
